import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, ArrowRight, ChartLineDown, Users, HandHeart, Lightning, ShieldCheck, Sparkle,
} from '@phosphor-icons/react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png';

export default function AboutUsPage() {
  return (
    <div className="min-h-screen bg-[#111111]">
      {/* Header */}
      <header className="bg-[#111111]/80 backdrop-blur-xl border-b border-[#222222] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="about-logo-link">
            <img src={LOGO_URL} alt="Divispend" className="h-8 sm:h-10 w-auto" />
          </Link>
          <Link to="/" className="text-sm text-[#999999] hover:text-white inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 sm:py-28 border-b border-[#222222]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#22C55E] mb-4">About Divispend</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6 leading-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Built for a world where<br />one paycheck isn't enough
          </h1>
          <p className="text-base sm:text-lg text-[#999999] max-w-2xl mx-auto leading-relaxed">
            We're here to help you turn your hustle into a real income stream — without permission, paperwork, or a second job that drains the life out of you.
          </p>
        </div>
      </section>

      {/* Why we exist */}
      <section className="py-20 border-b border-[#222222]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#22C55E] mb-4">Why We Exist</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-8 leading-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
            The economy changed. The rules didn't.
          </h2>

          <div className="space-y-6 text-[#cccccc] text-base sm:text-lg leading-relaxed">
            <p>
              Inflation has eaten paychecks alive. Rent, groceries, gas, healthcare — every line item creeps up while wages crawl. Political uncertainty has made markets unpredictable, retirement plans shaky, and "stable" jobs feel anything but stable. Even the upper-middle class — people doing everything right — now find themselves picking up side gigs to keep up.
            </p>
            <p>
              The reality is this: <span className="text-white font-semibold">a single income stream is no longer a safety net.</span> It's a single point of failure. And the existing platforms charging double-digit fees, locking out international users, demanding business filings, or capping how you can sell weren't built for the people doing the actual work.
            </p>
            <p>
              That's where we come in.
            </p>
          </div>
        </div>
      </section>

      {/* What we do */}
      <section className="py-20 border-b border-[#222222]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#22C55E] mb-4">What We Do</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-6 leading-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
                Every income stream, on one platform
              </h2>
              <p className="text-[#999999] text-base sm:text-lg leading-relaxed">
                Divispend doesn't pick a lane. Whether you're selling products, booking appointments, running subscriptions, accepting tips, crowdfunding a project, or letting fans invest in your business — we handle the money, the paperwork, and the payouts. You focus on the work.
              </p>
            </div>
            <div className="space-y-4">
              {[
                { icon: ChartLineDown, label: 'No fee gouging', text: 'Transparent platform fees, never per-transaction surprises' },
                { icon: Lightning, label: 'No setup hurdles', text: 'Sign up with an email; start selling in minutes' },
                { icon: HandHeart, label: 'No income ceiling', text: 'Run as many income streams as you want, on one account' },
                { icon: ShieldCheck, label: 'No platform lock-in', text: 'Export your data, your customers, your earnings — anytime' },
              ].map((p) => (
                <div key={p.label} className="flex items-start gap-4 bg-[#0d150e] border border-[#22C55E]/10 rounded-2xl p-5">
                  <div className="w-10 h-10 rounded-lg bg-[#22C55E]/15 flex items-center justify-center shrink-0">
                    <p.icon className="w-5 h-5 text-[#22C55E]" weight="duotone" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm mb-0.5" style={{ fontFamily: 'Cabinet Grotesk' }}>{p.label}</p>
                    <p className="text-[#999999] text-sm">{p.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission statement */}
      <section className="py-20 border-b border-[#222222]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <Sparkle className="w-10 h-10 text-[#22C55E] mx-auto mb-6" weight="fill" />
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#22C55E] mb-4">Our Mission</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-6 leading-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Income should never be limited by a platform's imagination.
          </h2>
          <p className="text-[#999999] text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
            We're building the financial infrastructure for everyone who refuses to settle for one paycheck. The chef with a cookbook. The therapist with a coaching course. The musician with a tip jar. The graphic designer running a Patreon. The mechanic with a Saturday side-hustle. They all deserve professional tools — and we built them.
          </p>
        </div>
      </section>

      {/* Team CTA */}
      <section className="py-20 border-b border-[#222222]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-br from-[#0D1F17] to-[#111111] border border-[#22C55E]/20 rounded-2xl p-8 sm:p-12 flex flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-10">
            <div className="flex-1">
              <Users className="w-10 h-10 text-[#22C55E] mb-3" weight="duotone" />
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>Meet the team</h3>
              <p className="text-[#999999] text-sm sm:text-base">The founders, operators, and builders behind Divispend.</p>
            </div>
            <Link to="/team">
              <Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm h-12 px-6 font-semibold whitespace-nowrap" data-testid="about-team-cta">
                See our team <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Start your second income today
          </h2>
          <p className="text-[#999999] mb-8 max-w-md mx-auto">
            Free to join. No business filings required. Just sign up and start earning.
          </p>
          <Link to="/register">
            <Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm h-12 px-8 text-base font-semibold" data-testid="about-cta-button">
              Create free account <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#222222] py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src={LOGO_URL} alt="Divispend" className="h-8 w-auto" />
          <p className="text-sm text-[#999999]">&copy; 2026 Divispend. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
